/**
 * PaymentInvoiceCard —— 客户详情页【收款 / 发票】合一卡（第2批纯前端合页聚合）。
 *
 * 外壳 .flow-card，内含两段 SectionCard：
 *   ① 收款段（key=5_deposit）：列待收单 + 就地「上传收款凭证」开 PaymentRecordModal（复用零改）。
 *   ② 发票段（key=4_invoice）：内嵌 <InvoiceWorkflowCard embedded/>（去外壳避免双层卡）。
 *
 * 不动 sales-flow 状态机 / step 值 / 任何后端端点 / 审批主体；仅前端聚合现有能力。
 * 徽章口径、段展开逻辑严格按 phase 契约实现（见下方注释）。
 */
import { useEffect, useState } from 'react';
import { Modal, Input, message } from 'antd';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import { useHasPermission } from '../../lib/permissions';
import { Btn } from '../ui';
import { PaymentRecordModal } from '../PaymentRecordModal';
import { InvoiceWorkflowCard } from '../InvoiceWorkflowCard';
import { SectionCard, SectionBadge } from './SectionCard';
import './fulfillment.css';

interface Props {
  customerId: number;
  customerName: string;
  /** 客户当前销售流程步（useCustomerData.salesFlowStep），用于判当前段/置灰未到段 */
  salesFlowStep?: string;
  /** 进度条点击联动的当前激活段 key（命中本段→展开 + 滚动到位） */
  activeStepKey?: string;
  /** 卡内操作完成后回调父层（拉详情即时刷新徽章/金额） */
  onRefresh?: () => void;
}

/* 段顺序口径（契约 ORDER）：用于 curIdx 比较，判 done/current/pending 三态 */
const ORDER = ['3_contract', '5_deposit', '4_invoice', '6_shipment', '7_materials', '8_order'];

/* 本卡两段的 step key */
const KEY_DEPOSIT = '5_deposit';
const KEY_INVOICE = '4_invoice';

export function PaymentInvoiceCard({
  customerId, customerName, salesFlowStep, activeStepKey, onRefresh,
}: Props) {
  const qc = useQueryClient();
  const me = useAuthStore.getState().user;

  // ── 收款段数据：复用后端 /payments?customerId 过滤（自带 role scope，sales 仅自己/manager 团队）
  const { data: payData } = useQuery<any>({
    queryKey: ['customer-payments', customerId],
    queryFn: () => $.get('/payments', { customerId, pageSize: 50 }),
  });
  const payments: any[] = payData?.items ?? [];

  // ── 收款段就地审核（项1）：拉「待我审」审批，queryKey/路径与 Approvals.tsx 逐字对齐（复用缓存、不另起请求）
  // ★权限点按后端三端点各自守卫拆分★：GET /approvals/pending=approval:view、approve=approval:approve、reject=approval:reject
  //   （seed 基线三点同授，但自定义 RBAC 可能只授其一；取数用 view 匹配端点真实守卫，避免持 approve 无 view 被 403 掩成 500）
  const canViewApproval = useHasPermission('approval:view');
  const canApprove = useHasPermission('approval:approve');
  const canReject = useHasPermission('approval:reject');
  const { data: apprData } = useQuery<any>({
    queryKey: ['approvals'],
    queryFn: () => $.get('/approvals/pending'),
    enabled: canViewApproval && canApprove,    // 匹配端点真实守卫(view) + 有 approve 权才渲染就地审核块
    refetchInterval: 15000,                     // 与 Approvals.tsx 对齐：停在客户详情也能随他处审批结果刷新
  });
  const approvals: any[] = apprData?.items ?? [];

  // 只取收款流水审批；metadataJson 可能是对象或 JSON 字符串（防御 parse），按 metadata.paymentId 分组到对应收款行
  const pendingByPaymentId: Record<number, any[]> = (() => {
    const map: Record<number, any[]> = {};
    for (const a of approvals) {
      if (a.entityType !== 'payment_record') continue;
      let meta: any = a.metadataJson;
      if (typeof meta === 'string') { try { meta = JSON.parse(meta); } catch { meta = null; } }
      const pid = Number(meta?.paymentId);
      if (!pid) continue;
      (map[pid] ??= []).push({ ...a, _meta: meta });    // 同一 paymentId 多笔 pending 逐条入列
    }
    return map;
  })();

  // ── 发票段徽章用轻量 query：与 InvoiceWorkflowCard 内部【同 queryKey】复用缓存，不产生重复请求
  const { data: invData } = useQuery<any>({
    queryKey: ['customer-invoices', customerId],
    queryFn: () => $.get('/invoices', { customerId, pageSize: 50 }),
  });
  const invoices: any[] = invData?.items ?? [];

  // ── 当前进度索引：salesFlowStep 在 ORDER 中的位置（completed/未知 → -1，下方按 step==='completed' 另判 done）
  const curIdx = ORDER.indexOf(salesFlowStep ?? '');
  const isCompleted = salesFlowStep === 'completed';

  // ── 录款弹窗 state（就地操作，复用 PaymentRecordModal 零改）
  const [recordingPayment, setRecordingPayment] = useState<any | null>(null);

  /* 录款权限门控（仿 AllListPages.canRecordPayment）：
   *   admin/director/manager 全可；sales 仅 row.ownerId===me.id；academic 不渲染按钮 */
  const canRecordPayment = (row: any): boolean => {
    if (!me) return false;
    if (me.role === 'admin' || me.role === 'director' || me.role === 'manager') return true;
    if (me.role === 'sales') return Number(row.ownerId) === Number(me.id);
    return false;
  };

  // ── 收款段徽章：先看本段相对当前步的位置，再在「当前步」内按单据细分
  const depositBadge: SectionBadge = (() => {
    const idx = ORDER.indexOf(KEY_DEPOSIT);
    if (isCompleted || (curIdx >= 0 && idx < curIdx)) return 'done';   // 已过本段 → ✅已完成
    if (curIdx >= 0 && idx > curIdx) return 'pending';                 // 未到本段 → ⚪未开始
    // idx===curIdx（当前在收款段）：按金额口径细分（paymentStatusFromAmounts 思路，按金额非后端 status，防误标）
    if (payments.length === 0) return 'pending';                       // 无收款单 → 未开始
    // 徽章「完成」纯按金额口径（对齐 AllListPages.paymentStatusFromAmounts：due>0 且 paid>=due）；
    // ★去掉 r.status==='completed' 支★——后端 status 曾把陈默误标 completed（R-徽章双源警告），不可信
    const anyCompleted = payments.some((r) => {
      const due = Number(r.totalDue ?? 0); const paid = Number(r.totalPaid ?? 0);
      return due > 0 && paid >= due;                                   // 有收齐单 → 完成
    });
    if (anyCompleted) return 'done';
    return 'current';                                                  // 未收齐 → 🔵进行中（列表行不带审批态，approval 无法精确，保守用 current）
  })();

  // ── 发票段徽章：按 /invoices?customerId 的 requestStatus 聚合
  const invoiceBadge: SectionBadge = (() => {
    const idx = ORDER.indexOf(KEY_INVOICE);
    if (isCompleted || (curIdx >= 0 && idx < curIdx)) return 'done';
    if (curIdx >= 0 && idx > curIdx) return 'pending';
    // idx===curIdx（当前在发票段）：按发票申请态聚合
    if (invoices.length === 0) return 'pending';                       // 无发票 → 未开始
    const st = invoices.map((v) => v.requestStatus);
    if (st.some((s) => s === 'confirmed')) return 'done';              // 有已确认 → ✅
    if (st.some((s) => s === 'pending_open')) return 'approval';       // 申请中(待财务开) → 🟡
    if (st.some((s) => s === 'opened' || s === 'sent_to_customer')) return 'current'; // 已开/已发 → 🔵
    return 'current';
  })();

  // 「未到段」判定（用于置灰 disabled）：本段 idx > curIdx
  const depositLocked = curIdx >= 0 && ORDER.indexOf(KEY_DEPOSIT) > curIdx && !isCompleted;
  const invoiceLocked = curIdx >= 0 && ORDER.indexOf(KEY_INVOICE) > curIdx && !isCompleted;
  // 「当前段」判定（品牌蓝左边线高亮）：本段 idx === curIdx
  const depositCurrent = curIdx >= 0 && ORDER.indexOf(KEY_DEPOSIT) === curIdx && !isCompleted;
  const invoiceCurrent = curIdx >= 0 && ORDER.indexOf(KEY_INVOICE) === curIdx && !isCompleted;

  // ── 段展开（受控）：当前步默认展开 / done 段默认收起 / 监听 activeStepKey 命中本段时展开 / 用户手动 toggle
  const [depositOpen, setDepositOpen] = useState<boolean>(depositCurrent);
  const [invoiceOpen, setInvoiceOpen] = useState<boolean>(invoiceCurrent);

  // 当前步/完成态变化时，校准默认展开（当前段展开、done 段收起）
  // 依赖加 customerId：切客户串台时（同一卡复用、salesFlowStep 可能不变）也重算默认展开
  useEffect(() => {
    setDepositOpen(depositCurrent);
    setInvoiceOpen(invoiceCurrent);
    // 仅按 salesFlowStep/customerId 变化重算默认；用户手动 toggle 后的态由下方 activeStepKey/手动覆盖
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesFlowStep, customerId]);

  // 进度条点击联动：activeStepKey 命中本段 → 展开
  // isPending 守卫（!Locked，即 idx<=curIdx 已到/在本段）：未到段（pending）不强开，仅置灰只读
  useEffect(() => {
    if (activeStepKey === KEY_DEPOSIT && !depositLocked) setDepositOpen(true);
    if (activeStepKey === KEY_INVOICE && !invoiceLocked) setInvoiceOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepKey]);

  // ── 就地审核成功后：刷新收款段 + 审批列表（对齐 A 的 key）+ 客户详情，徽章/金额/待审即时更新
  const refreshApprovals = () => {
    qc.invalidateQueries({ queryKey: ['customer-payments', customerId] });
    qc.invalidateQueries({ queryKey: ['approvals'] });
    qc.invalidateQueries({ queryKey: ['customer', String(customerId)] });
    onRefresh?.();
  };

  // ── 录款成功后：补 invalidate 让合页徽章 + 详情金额即时刷新（PaymentRecordModal 本体只 invalidate 全局 /payments）
  const handleRecordClose = () => {
    setRecordingPayment(null);
    qc.invalidateQueries({ queryKey: ['customer-payments', customerId] });
    qc.invalidateQueries({ queryKey: ['customer', String(customerId)] });
    onRefresh?.();
  };

  return (
    <div className="flow-card">
      <div style={{ padding: '10px 14px 2px', fontSize: 15, fontWeight: 800, color: 'var(--text-1)' }}>
        💰 收款 + 发票
      </div>

      {/* ── 收款段 ───────────────────────────────────────── */}
      <SectionCard
        id={`flow-sec-${KEY_DEPOSIT}`}
        title="💵 收款"
        badge={depositBadge}
        current={depositCurrent}
        disabled={depositLocked}
        expanded={depositOpen}
        onToggle={() => setDepositOpen((v) => !v)}
      >
        {/* 段内小字：审批去向标注（收款凭证 → 直属主管审核） */}
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 10 }}>
          收款凭证 → 直属主管审核
        </div>

        {payments.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--text-3)', padding: '6px 0' }}>
            暂无收款单。进入收款步后系统会按合同自动建单。
          </div>
        )}

        {payments.map((row) => {
          const due = Number(row.totalDue ?? 0);
          const paid = Number(row.totalPaid ?? 0);
          const balance = Number(row.balanceDue ?? Math.max(0, due - paid));
          // 仅未结清(balanceDue>0 且未 completed) + 有权 + 本段未置灰，才显「上传收款凭证」
          const owing = balance > 0 && row.status !== 'completed';
          const showRecord = owing && canRecordPayment(row) && !depositLocked;
          // 本收款行待我审的收款流水审批（按 metadataJson.paymentId===row.id 匹配；同一行多笔逐条列）
          const rowPending = (canViewApproval && canApprove) ? (pendingByPaymentId[Number(row.id)] ?? []) : [];
          return (
            <div key={row.id} style={{ padding: '10px 0', borderTop: '1px solid var(--border-light)' }}>
              <div className="flow-row" style={{
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              }}>
                {/* 应收 / 已收 / 待收 三金额 */}
                <div style={{ display: 'flex', gap: 16, flex: '1 1 auto', minWidth: 0 }}>
                  <Money label="应收" value={due} />
                  <Money label="已收" value={paid} tone="success" />
                  <Money label="待收" value={balance} tone={balance > 0 ? 'warning' : undefined} />
                </div>
                {/* 就地「上传收款凭证」：复用 PaymentRecordModal（row 已带 {id,customerId,totalDue,totalPaid,balanceDue,orderType}） */}
                {showRecord && (
                  <Btn kind="primary" onClick={() => setRecordingPayment(row)}>
                    上传收款凭证
                  </Btn>
                )}
              </div>
              {/* 就地审核区：对齐资料段 InlineMaterialApprove/Reject 风格，逐条列「待我审核 ¥金额」+ ✓通过/✗驳回 */}
              {rowPending.map((a) => (
                <div key={a.id} className="flow-row" style={{
                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8,
                }}>
                  <span style={{ fontSize: 13, color: 'var(--text-2)', flex: '1 1 auto', minWidth: 0 }}>
                    待我审核 ¥ {Number(a._meta?.amount ?? 0).toLocaleString()}
                  </span>
                  <InlinePaymentApprove approval={a} customerId={customerId} qcRefresh={refreshApprovals} />
                  {canReject && <InlinePaymentReject approval={a} customerId={customerId} qcRefresh={refreshApprovals} />}
                </div>
              ))}
            </div>
          );
        })}
      </SectionCard>

      {/* ── 发票段 ───────────────────────────────────────── */}
      <SectionCard
        id={`flow-sec-${KEY_INVOICE}`}
        title="📋 发票"
        badge={invoiceBadge}
        current={invoiceCurrent}
        disabled={invoiceLocked}
        expanded={invoiceOpen}
        onToggle={() => setInvoiceOpen((v) => !v)}
      >
        {/* 段内小字：审批去向标注（发票图 → 财务审核） */}
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 10 }}>
          发票图 → 财务审核
        </div>
        {/* 内嵌发票全能力组件（embedded 去外壳避免双层卡）；defaultAmount 用客户应收口径，与收款段一致 */}
        <InvoiceWorkflowCard
          embedded
          customerId={customerId}
          customerName={customerName}
          defaultAmount={payments.reduce((s, r) => s + Number(r.totalDue ?? 0), 0) || undefined}
        />
      </SectionCard>

      {/* 录款弹窗：复用零改，成功后由 onClose 补 invalidate（PaymentRecordModal 内部已 message+提交） */}
      <PaymentRecordModal
        open={!!recordingPayment}
        payment={recordingPayment}
        onClose={handleRecordClose}
      />
    </div>
  );
}

/** 就地通过收款审批：二次确认 POST /approvals/:id/approve（与 Approvals.tsx 完全同款端点）。 */
function InlinePaymentApprove({
  approval, customerId, qcRefresh,
}: { approval: any; customerId: number; qcRefresh: () => void }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => $.post(`/approvals/${approval.id}/approve`, {}),
    onSuccess: () => {
      message.success('已通过');
      qc.invalidateQueries({ queryKey: ['customer-payments', customerId] });
      qcRefresh();
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '操作失败'),
  });
  return (
    <Btn kind="primary" disabled={mut.isPending} onClick={() => {
      if (mut.isPending) return;
      Modal.confirm({
        title: '通过收款审核？', content: `确认通过收款 ¥${Number(approval._meta?.amount ?? 0).toLocaleString()}？`,
        okText: '通过', cancelText: '取消', onOk: () => mut.mutateAsync(),
      });
    }}>
      {mut.isPending ? '处理中…' : '✓ 通过'}
    </Btn>
  );
}

/** 就地驳回收款审批：TextArea 必填理由 → POST /approvals/:id/reject {reason}（复刻 Approvals.tsx 的 reject Modal）。 */
function InlinePaymentReject({
  approval, customerId, qcRefresh,
}: { approval: any; customerId: number; qcRefresh: () => void }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const mut = useMutation({
    mutationFn: () => $.post(`/approvals/${approval.id}/reject`, { reason: reason.trim() }),
    onSuccess: () => {
      message.success('已驳回');
      setOpen(false); setReason('');
      qc.invalidateQueries({ queryKey: ['customer-payments', customerId] });
      qcRefresh();
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '驳回失败'),
  });
  return (
    <>
      <Btn kind="default" onClick={() => setOpen(true)}>✗ 驳回</Btn>
      <Modal title="驳回收款审核" open={open}
        onCancel={() => { setOpen(false); setReason(''); }}
        onOk={() => { if (!reason.trim()) { message.error('请填写驳回理由'); return; } mut.mutate(); }}
        okText={mut.isPending ? '提交中…' : '确认驳回'} okButtonProps={{ danger: true }} cancelText="取消" confirmLoading={mut.isPending}>
        <Input.TextArea autoFocus value={reason} onChange={(e) => setReason(e.target.value)} rows={4}
          placeholder="请填写驳回理由（必填）— 销售员看得到，请写清楚原因" />
      </Modal>
    </>
  );
}

/* 小金额块：标签 + ¥ 数值（复刻 AllListPages moneyStack 的着色口径） */
function Money({ label, value, tone }: { label: string; value: number; tone?: 'success' | 'warning' }) {
  const color = tone === 'success' ? 'var(--success-text)'
    : tone === 'warning' ? 'var(--warning-text)'
    : 'var(--text-1)';
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color }}>¥ {Number(value).toLocaleString()}</div>
    </div>
  );
}
